import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "./lib/auth";
import { AppLayout } from "./components/AppLayout";

// Login + accept-invite tetap eager — dipakai sebelum shell app dimuat.
import { LoginPage } from "./pages/LoginPage";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";

// Semua page authenticated di-lazy load (per-route chunk).
const OverviewPage = lazy(() =>
  import("./pages/OverviewPage").then((m) => ({ default: m.OverviewPage })),
);
const QuickstartPage = lazy(() =>
  import("./pages/QuickstartPage").then((m) => ({ default: m.QuickstartPage })),
);
const MessagesPage = lazy(() =>
  import("./pages/MessagesPage").then((m) => ({ default: m.MessagesPage })),
);
const MessageDetailPage = lazy(() =>
  import("./pages/MessageDetailPage").then((m) => ({
    default: m.MessageDetailPage,
  })),
);
const DomainsPage = lazy(() =>
  import("./pages/DomainsPage").then((m) => ({ default: m.DomainsPage })),
);
const ApiKeysPage = lazy(() =>
  import("./pages/ApiKeysPage").then((m) => ({ default: m.ApiKeysPage })),
);
const WebhooksPage = lazy(() =>
  import("./pages/WebhooksPage").then((m) => ({ default: m.WebhooksPage })),
);
const SuppressionsPage = lazy(() =>
  import("./pages/SuppressionsPage").then((m) => ({
    default: m.SuppressionsPage,
  })),
);
const TemplatesPage = lazy(() =>
  import("./pages/TemplatesPage").then((m) => ({ default: m.TemplatesPage })),
);
const TeamPage = lazy(() =>
  import("./pages/TeamPage").then((m) => ({ default: m.TeamPage })),
);
const BillingPage = lazy(() =>
  import("./pages/BillingPage").then((m) => ({ default: m.BillingPage })),
);
const AuditPage = lazy(() =>
  import("./pages/AuditPage").then((m) => ({ default: m.AuditPage })),
);
const ValidatorPage = lazy(() =>
  import("./pages/ValidatorPage").then((m) => ({ default: m.ValidatorPage })),
);
const IpsPage = lazy(() =>
  import("./pages/IpsPage").then((m) => ({ default: m.IpsPage })),
);
const OpsIpsPage = lazy(() =>
  import("./pages/OpsIpsPage").then((m) => ({ default: m.OpsIpsPage })),
);
const InboundRoutesPage = lazy(() =>
  import("./pages/InboundRoutesPage").then((m) => ({
    default: m.InboundRoutesPage,
  })),
);
const InboundMessagesPage = lazy(() =>
  import("./pages/InboundMessagesPage").then((m) => ({
    default: m.InboundMessagesPage,
  })),
);
const BatchesPage = lazy(() =>
  import("./pages/BatchesPage").then((m) => ({ default: m.BatchesPage })),
);
const SubAccountsPage = lazy(() =>
  import("./pages/SubAccountsPage").then((m) => ({
    default: m.SubAccountsPage,
  })),
);

function PageFallback() {
  return (
    <div className="h-64 flex items-center justify-center text-slate-400">
      <Loader2 size={20} className="animate-spin" />
    </div>
  );
}

export function App() {
  const { me, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route element={<AppLayout />}>
        <Route
          path="/"
          element={
            <Suspense fallback={<PageFallback />}>
              <OverviewPage />
            </Suspense>
          }
        />
        <Route
          path="/quickstart"
          element={
            <Suspense fallback={<PageFallback />}>
              <QuickstartPage />
            </Suspense>
          }
        />
        <Route
          path="/messages"
          element={
            <Suspense fallback={<PageFallback />}>
              <MessagesPage />
            </Suspense>
          }
        />
        <Route
          path="/messages/:id"
          element={
            <Suspense fallback={<PageFallback />}>
              <MessageDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/domains"
          element={
            <Suspense fallback={<PageFallback />}>
              <DomainsPage />
            </Suspense>
          }
        />
        <Route
          path="/api-keys"
          element={
            <Suspense fallback={<PageFallback />}>
              <ApiKeysPage />
            </Suspense>
          }
        />
        <Route
          path="/webhooks"
          element={
            <Suspense fallback={<PageFallback />}>
              <WebhooksPage />
            </Suspense>
          }
        />
        <Route
          path="/templates"
          element={
            <Suspense fallback={<PageFallback />}>
              <TemplatesPage />
            </Suspense>
          }
        />
        <Route
          path="/team"
          element={
            <Suspense fallback={<PageFallback />}>
              <TeamPage />
            </Suspense>
          }
        />
        <Route
          path="/billing"
          element={
            <Suspense fallback={<PageFallback />}>
              <BillingPage />
            </Suspense>
          }
        />
        <Route
          path="/audit"
          element={
            <Suspense fallback={<PageFallback />}>
              <AuditPage />
            </Suspense>
          }
        />
        <Route
          path="/validator"
          element={
            <Suspense fallback={<PageFallback />}>
              <ValidatorPage />
            </Suspense>
          }
        />
        <Route
          path="/suppressions"
          element={
            <Suspense fallback={<PageFallback />}>
              <SuppressionsPage />
            </Suspense>
          }
        />
        <Route
          path="/ips"
          element={
            <Suspense fallback={<PageFallback />}>
              <IpsPage />
            </Suspense>
          }
        />
        <Route
          path="/inbound/routes"
          element={
            <Suspense fallback={<PageFallback />}>
              <InboundRoutesPage />
            </Suspense>
          }
        />
        <Route
          path="/inbound/messages"
          element={
            <Suspense fallback={<PageFallback />}>
              <InboundMessagesPage />
            </Suspense>
          }
        />
        <Route
          path="/batches"
          element={
            <Suspense fallback={<PageFallback />}>
              <BatchesPage />
            </Suspense>
          }
        />
        <Route
          path="/sub-accounts"
          element={
            <Suspense fallback={<PageFallback />}>
              <SubAccountsPage />
            </Suspense>
          }
        />
        {me?.isPlatformAdmin && (
          <Route
            path="/ops/ips"
            element={
              <Suspense fallback={<PageFallback />}>
                <OpsIpsPage />
              </Suspense>
            }
          />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
